import { useMemo, useState } from "react";
import { toast } from "@/components/Toast";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useCarryForwardMutation, useGetFiscalYearDetails, useGetNetProfitLoss, useGetTrialBalance, useLockYearMutation, useOpenNewYearMutation, useReopenYearMutation } from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { actionsKeys, getBaseRoute, permissionActions } from "@/lib/utils";
import { useLocation } from "react-router-dom";

const useActions = () => {
  const queryClient = useQueryClient();
  const [reopenText, setReopenText] = useState<any>({
    reason: "",
    confirmText: "",
    isReversed: false,
  });
  const [openReopenDialog, setOpenReopenDialog] = useState<boolean>(false);
  const [openLockDialog, setOpenLockDialog] = useState<boolean>(false);
  const [openNewYearDialog, setOpenNewYearDialog] = useState<boolean>(false);
  const [openCarryForwardDialog, setOpenCarryForwardDialog] = useState<any>({
    step1: false,
    step2: false,
    step3: false
  })
  const {communityData, currentFinanceYear, fiscalYearDtl, accessPermission} = useSelector((state: RootState) => state.currentData)
  const communityId = communityData?.communityId;

  const basePath = getBaseRoute(useLocation().pathname);
  const isCam = basePath?.toLowerCase() == "cam";
  const isOwner = basePath?.toLowerCase() == "owner";

  const hasEditAccess = useMemo(() => {
    return isCam || isOwner ? accessPermission?.[actionsKeys?.FISCAL_YEAR?.toLowerCase()]?.[permissionActions.UPDATE] : true
  }, [accessPermission]);

  const isCurrentYearLock = useMemo(() => {
    if (fiscalYearDtl) {
      return fiscalYearDtl?.status?.toLowerCase() == "locked"
    }
  }, [fiscalYearDtl]);

  const isCurrentYearClosed = useMemo(() => {
    if (fiscalYearDtl) {
      return fiscalYearDtl?.status?.toLowerCase() == "closed"
    }
  }, [fiscalYearDtl]);

  const { data: fiscalYearData, isLoading: isFiscalDataLoading } = useGetFiscalYearDetails({
    communityId,
    fiscalYear: (Number(currentFinanceYear) + 1)
  });
  
  const {data: trialBalanceData, isRefetching: isLoadingTrialBalData, refetch: refetchTrialBalance} = useGetTrialBalance({
    communityId,
    fiscalYear: currentFinanceYear,
    queryParams: {
      enabled: false
    }
  });

  const {data: netProfitLoss, isRefetching: isLoadingNetProfitLoss, refetch: refetchNetProfitLoss} = useGetNetProfitLoss({
    communityId,
    fiscalYear: currentFinanceYear,
    queryParams: {
      enabled: false
    }
  });

  const refetchQueries = () => {
    queryClient.invalidateQueries({queryKey: ["fiscal-year-dtl", communityId, currentFinanceYear]})
    queryClient.invalidateQueries({queryKey: ["operational-logs", communityId, currentFinanceYear]})
  }

  const { mutate: lockYearMutate, isPending: isLocking } =
    useLockYearMutation({
      onSuccess: () => {
        toast.success("Close Year (Lock Year) done successfully.");
        refetchQueries();
        setOpenLockDialog(false);
      },
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        queryClient.invalidateQueries({queryKey: ["fiscal-year-dtl", communityId, currentFinanceYear]})
        setOpenLockDialog(false);
        return error?.response?.data?.message;
      },
    });


  const { mutate: reopenYearMutate, isPending: isReopening } =
    useReopenYearMutation({
      onSuccess: () => {
        toast.success("Fiscal Year reopened successfully.");
        refetchQueries();
        setReopenText({
          reason: "",
          confirmText: "",
          isReversed: false
        })
        setOpenReopenDialog(false);
      },
      onError: (error: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
      },
    });


  const { mutate: openNewYearMutate, isPending: isOpenNewYearPending } =
    useOpenNewYearMutation({
      onSuccess: () => {
        toast.success("New Fiscal Year opened successfully");
        refetchQueries();
        setOpenNewYearDialog(false);
      },
      onError: (error: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
      },
    });


  const { mutate: carryForwardMutate, isPending: isCarryForwardPending } =
    useCarryForwardMutation({
      onSuccess: () => {
        toast.success("Carry Forward completed successfully");
        refetchQueries();
        setOpenCarryForwardDialog((prev: any) => {
          // Create a new object with all keys set to false
          const reset = Object.keys(prev).reduce((acc, k) => {
            acc[k] = false;
            return acc;
          }, {} as any);
          
          // Set the clicked key to true
          return {
            ...reset,
          };
        })
      },
      onError: (error: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
      },
    });

  // --------------------------------- Lock Year --------------------------------------
  const onLockYear = () => {
    setOpenLockDialog(true);
  }

  const onConfirmLockYear = () => {
    lockYearMutate({ communityId: communityData?.communityId, fiscalYear: currentFinanceYear });
  }

  // ------------------------------------------------------------------------------------

  const onConfirmCarryForward = () => {
    carryForwardMutate({ communityId: communityData?.communityId, fiscalYear: currentFinanceYear });
  };

  const onCarryForward = () => {
    refetchTrialBalance();
    setOpenCarryForwardDialog((prev: any) => ({
      ...prev,
      step1: true
    }))
  }

  const onNextCarryForward = (key: string) => {
    if (key == "step2") {
      refetchNetProfitLoss();
    }
    setOpenCarryForwardDialog((prev: any) => {
      // Create a new object with all keys set to false
      const reset = Object.keys(prev).reduce((acc, k) => {
        acc[k] = false;
        return acc;
      }, {} as any);
      
      // Set the clicked key to true
      return {
        ...reset,
        [key]: true
      };
    })
  };
  
  const onReopenFiscalYear = () => {
    setReopenText({
      reason: "",
      confirmText: "",
      isReversed: false,
    })
    setOpenReopenDialog(true);
  }

  const onReopenReverseFiscalYear = () => {
    setReopenText({
      reason: "",
      confirmText: "",
      isReversed: true,
    })
    setOpenReopenDialog(true);
  }

  const onConfirmReopen = () => {
    const {isReversed, confirmText, reason} = reopenText;
    const expected = isReversed  ? "REVERSE" : "REOPEN";

    if (confirmText !== expected) {
      toast.error(`Please type ${expected}`);
      return;
    }

    if (!reason.trim()) {
      toast.error("Please enter a reason for reopening.");
      return;
    }

    reopenYearMutate({
      communityId: communityData?.communityId,
      fiscalYear: currentFinanceYear,
      data: { reason },
    });
  };

  const onChangeReopenText = (key: string, value: string) => {
    setReopenText((prev: any) => ({
      ...prev,
      [key]: value
    }))
  }

  const onOpenNewYear = () => {
    setOpenNewYearDialog(true);
  }

  const onConfirmOpenNewYear = () => {
    openNewYearMutate({ communityId: communityData?.communityId, data: {
        fiscalYear: Number(currentFinanceYear) + 1
      } 
    });
  };

  return {
    reopenText,
    setReopenText,
    isLocking,
    isReopening,
    openLockDialog,
    currentFinanceYear,
    onLockYear,
    onConfirmLockYear,
    setOpenLockDialog,
    isCurrentYearLock,
    isCurrentYearClosed,
    isOpenNewYearPending,
    isCarryForwardPending,
    onCarryForward,
    openCarryForwardDialog,
    setOpenCarryForwardDialog,
    onNextCarryForward,
    isLoadingTrialBalData,
    trialBalanceData: (trialBalanceData as any)?.data,
    isLoadingNetProfitLoss,
    netProfitLoss: (netProfitLoss as any)?.data,
    onConfirmCarryForward,
    onReopenFiscalYear,
    onReopenReverseFiscalYear,
    openReopenDialog,
    setOpenReopenDialog,
    onConfirmReopen,
    onChangeReopenText,
    onOpenNewYear,
    onConfirmOpenNewYear,
    openNewYearDialog,
    setOpenNewYearDialog,
    isFiscalDataLoading,
    fiscalYearData,
    hasEditAccess
  };
};

export default useActions;